import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ERROR_LABELS, INSTRUMENTATION_KEYS, VIEW_STATE } from '../../../../constants/LTSConstants';
import { LTSContext } from '../../../../context/LTSContext';
import usePopularTrains from '../../../../hooks/usePopularTrains';
import { fetchTrainsList } from '../../../../utils/ApiHandlers';
import { debounce } from '../../../../utils/LTSUtils';
import { TrainDetail } from '../../../../types/LTSTypes';
import RecentSearchedTrainsList from '../RecentSearchedTrainsList/RecentSearchedTrainsList.component';
import PopularTrainsList from '../PopularTrainsList/PopularTrainsList.component';
import SearchedTrainsList from '../SearchedTrainsList/SearchedTrainsList.component';
import ErrorView from '../../../../components/ErrorView/ErrorView.component';
import PropTypes from 'prop-types';
import { LTS_LANDING_INSTRUMENTATION_PAGE_NAME } from '../../../../constants/SEOConstants';
import { trackClickEventProp61 } from '../../../../../../utils/omnitureUtils';

export const AutoSuggestTrainsList = (props) => {
  const { searchQuery, handleOnTrainSelected } = props;

  // ---------- hooks ----------
  const popularTrains = usePopularTrains();

  // ---------- context ----------
  const ltsContext = useContext(LTSContext);
  const { recentSearchTrains, onTrainSelected } = ltsContext;

  // ---------- refs ----------
  const abortController = useRef<AbortController | undefined>();
  const fetchTrainListFuncRef = useRef<() => void>();

  // ---------- state ----------
  const [query, setQuery] = useState('');
  const [viewState, setViewState] = useState(VIEW_STATE.DEFAULT);
  const [trainList, setTrainList] = useState<any[]>([]);

  // ---------- functions ----------

  useEffect(() => {
    setQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (query.length > 2) {
      fetchTrainListFunc(query);
    } else {
      setViewState(VIEW_STATE.DEFAULT);
    }
  }, [query]);

  fetchTrainListFuncRef.current = async () => {
    if (query.length <= 2) {
      return;
    }

    let signal: AbortSignal = {} as AbortSignal;

    try {
      if (abortController.current) {
        abortController.current.abort();
      }
      abortController.current = new AbortController();
      signal = abortController.current.signal;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('Error while initializing AbortController');
    }

    const response = await fetchTrainsList(query, signal);

    abortController.current = undefined;

    if (response?.msg === 'AbortError') {
      return;
    }

    if (!response || !response.success || !response.data || !response.data.r) {
      setViewState(VIEW_STATE.ERROR);
      return;
    }

    if (response.data.r.length > 0) {
      setTrainList(response.data.r);
      setViewState(VIEW_STATE.SHOW_DETAIL);
      return;
    }

    setViewState(VIEW_STATE.NO_RESULT);
  };

  const fetchTrainListFunc = useCallback(
    debounce(() => {
      if (fetchTrainListFuncRef?.current) {
        fetchTrainListFuncRef.current();
      }
    }, 300),
    []
  );

  const OnTrainSelect = (trainDetail: TrainDetail) => {
    trackClickEventProp61(LTS_LANDING_INSTRUMENTATION_PAGE_NAME, INSTRUMENTATION_KEYS.trainSelectClick);
    onTrainSelected(trainDetail);
    handleOnTrainSelected();
  };

  const handleReportMissingTrain = () => {
    window.location.assign(
      `mailto:mobilefeedback@makemytrip.com?Subject=${ERROR_LABELS.missing_train_alert}&body=${ERROR_LABELS.train_not_found_email_body}`
    );
  };

  return (
    <Fragment>
      {viewState === VIEW_STATE.DEFAULT ? (
        <Fragment>
          {recentSearchTrains && recentSearchTrains?.length > 0 && (
            <RecentSearchedTrainsList trainList={recentSearchTrains} onClick={OnTrainSelect} />
          )}
          {popularTrains && popularTrains?.length > 0 && (
            <PopularTrainsList trainList={popularTrains} onClick={OnTrainSelect} />
          )}
        </Fragment>
      ) : null}
      {viewState === VIEW_STATE.NO_RESULT && (
        <ErrorView
          title={ERROR_LABELS.no_trains_found}
          subtitle={ERROR_LABELS.report_missing_train}
          onRetry={handleReportMissingTrain}
        />
      )}
      {viewState === VIEW_STATE.ERROR && <ErrorView title={ERROR_LABELS.something_went_wrong} />}
      {viewState === VIEW_STATE.SHOW_DETAIL && <SearchedTrainsList trainList={trainList} onClick={OnTrainSelect} />}
    </Fragment>
  );
};

AutoSuggestTrainsList.propTypes = {
  searchQuery: PropTypes.string,
  handleOnTrainSelected: PropTypes.func
};
